import { useState } from "react";
import axios from "axios";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinModal({
    open,
    onOpenChange,
    lumaEventUrl,
    lumaEventName,
}) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        role: "",
        reason: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (e) =>
        setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Please share your name and email.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/join`, form);
            setDone(true);
            toast.success("You're in. We'll be in touch.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Something went wrong. Try again in a moment."
            );
        } finally {
            setLoading(false);
        }
    };

    const close = () => {
        onOpenChange(false);
        setTimeout(() => {
            setDone(false);
            setForm({ name: "", email: "", role: "", reason: "" });
        }, 300);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
            <DialogContent
                key={lumaEventUrl || "default"}
                data-testid="join-modal"
                className="sm:max-w-[520px] p-0 overflow-hidden bg-white border-black/10 rounded-2xl"
            >
                <div className="p-8 md:p-10">
                    {done ? (
                        <div className="text-center py-6" data-testid="join-success">
                            <div className="h-14 w-14 rounded-full bg-[#E54D2E]/10 text-[#E54D2E] inline-flex items-center justify-center mb-5">
                                <CheckCircle2 className="h-7 w-7" />
                            </div>
                            <h3 className="font-display text-3xl font-black tracking-tight text-zinc-950">
                                Karibu, {form.name.split(" ")[0] || "builder"}.
                            </h3>
                            <p className="mt-3 text-zinc-600 leading-relaxed">
                                We received your registration. Keep an eye on
                                your inbox — we’ll send details for the next
                                cohort shortly.
                            </p>
                            {lumaEventUrl ? (
                                <a
                                    href={lumaEventUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="join-luma-link"
                                    className="mt-8 w-full inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#E54D2E] text-white font-medium hover:bg-zinc-950 transition-colors"
                                >
                                    Complete RSVP on Luma
                                    <ArrowRight className="h-4 w-4" />
                                </a>
                            ) : null}
                            <button
                                onClick={close}
                                data-testid="join-close-btn"
                                className={`inline-flex items-center gap-2 h-12 px-6 rounded-full border border-black/10 text-zinc-900 font-medium hover:bg-black/5 transition-colors ${lumaEventUrl ? "mt-3 w-full justify-center" : "mt-8 w-full justify-center bg-zinc-950 text-white hover:bg-[#E54D2E] border-0"}`}
                            >
                                {lumaEventUrl ? "Close" : "Done"}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <DialogHeader className="text-left space-y-3 mb-6">
                                <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[#E54D2E]">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#E54D2E]" />
                                    Register for event
                                </div>
                                <DialogTitle className="font-display text-3xl md:text-4xl font-black tracking-[-0.02em] text-zinc-950">
                                    Come build with us.
                                </DialogTitle>
                                <DialogDescription className="text-zinc-600 leading-relaxed">
                                    {lumaEventName ? (
                                        <>
                                            You’re registering for:{" "}
                                            <strong className="text-zinc-900">
                                                {lumaEventName}
                                            </strong>
                                            . Tell us a little about you —
                                            then finish your RSVP on Luma.
                                        </>
                                    ) : (
                                        <>
                                            Tell us a little about you. We’ll
                                            send details for the next session.
                                        </>
                                    )}
                                </DialogDescription>
                            </DialogHeader>
                            <form
                                onSubmit={onSubmit}
                                className="space-y-4"
                                data-testid="join-form"
                            >
                                <Field label="Full name" htmlFor="join-name">
                                    <Input
                                        id="join-name"
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        placeholder="Wanjiku Mwangi"
                                        required
                                        data-testid="join-name-input"
                                        className="h-12 rounded-xl border-black/10 bg-[#FAFAFA] focus-visible:ring-[#E54D2E]"
                                    />
                                </Field>
                                <Field label="Email" htmlFor="join-email">
                                    <Input
                                        id="join-email"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        placeholder="you@builder.dev"
                                        required
                                        data-testid="join-email-input"
                                        className="h-12 rounded-xl border-black/10 bg-[#FAFAFA] focus-visible:ring-[#E54D2E]"
                                    />
                                </Field>
                                <Field label="Your role" htmlFor="join-role">
                                    <Input
                                        id="join-role"
                                        name="role"
                                        value={form.role}
                                        onChange={onChange}
                                        placeholder="Developer · Designer · Founder"
                                        data-testid="join-role-input"
                                        className="h-12 rounded-xl border-black/10 bg-[#FAFAFA] focus-visible:ring-[#E54D2E]"
                                    />
                                </Field>
                                <Field
                                    label="Why do you want to join?"
                                    htmlFor="join-reason"
                                >
                                    <Textarea
                                        id="join-reason"
                                        name="reason"
                                        value={form.reason}
                                        onChange={onChange}
                                        placeholder="What are you building (or want to)?"
                                        rows={3}
                                        data-testid="join-reason-input"
                                        className="rounded-xl border-black/10 bg-[#FAFAFA] focus-visible:ring-[#E54D2E] resize-none"
                                    />
                                </Field>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="join-submit-btn"
                                    className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-zinc-950 text-white text-sm font-medium hover:bg-[#E54D2E] transition-colors disabled:opacity-60"
                                >
                                    {loading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <>
                                            Register
                                            <ArrowRight className="h-4 w-4" />
                                        </>
                                    )}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, htmlFor, children }) {
    return (
        <div className="space-y-1.5">
            <Label
                htmlFor={htmlFor}
                className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500"
            >
                {label}
            </Label>
            {children}
        </div>
    );
}
